import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Alert, Chip,
  Grid, Paper, Select, MenuItem, FormControl, InputLabel, Autocomplete,
  ToggleButton, ToggleButtonGroup, CircularProgress, useMediaQuery, useTheme
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, Add, Delete, Edit, Save, Cancel,
  CalendarMonth, ViewWeek, Today, Close, DragIndicator
} from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, subMonths, format, isSameMonth, isSameDay, getDay } from 'date-fns';
import axios from 'axios';

const VL_COLORS = [
  { light: '#fff8e1', dark: '#f9a825' }, { light: '#e3f2fd', dark: '#1976d2' },
  { light: '#e8f5e9', dark: '#388e3c' }, { light: '#f3e5f5', dark: '#7b1fa2' },
  { light: '#eceff1', dark: '#546e7a' }, { light: '#fce4ec', dark: '#c2185b' },
  { light: '#e0f7fa', dark: '#0097a7' }
];

const vlColorCache = {};
let colorIndex = 2;

const getVLColors = (vlName) => {
  if (!vlName) return { light: '#f5f5f5', dark: '#9e9e9e' };
  const name = vlName.toLowerCase().trim();
  if (name === 'openers' || name === 'op') return VL_COLORS[0];
  if (name === 'op+null' || name === 'opnull' || name.includes('op+null')) return VL_COLORS[1];
  if (vlColorCache[name]) return vlColorCache[name];
  const colors = VL_COLORS[colorIndex % VL_COLORS.length];
  vlColorCache[name] = colors;
  colorIndex++;
  return colors;
};

function DraggablePanel({ id, date, position, zIndex, onClose, onFocus, onPositionChange, children, isMobile }) {
  const panelRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    if (e.target.closest('.panel-content') || isMobile) return;
    setIsDragging(true);
    const rect = panelRef.current.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    onFocus(id);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const newX = Math.max(0, Math.min(window.innerWidth - 400, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragOffset.y));
      onPositionChange(id, { x: newX, y: newY });
    };
    const handleMouseUp = () => setIsDragging(false);
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp); };
  }, [isDragging, dragOffset, id, onPositionChange]);

  if (isMobile) {
    return (
      <Dialog open={true} onClose={() => onClose(id)} fullScreen>
        <DialogTitle sx={{ bgcolor: '#4869EE', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography fontWeight={600}>{date && format(date, 'EEE, dd MMM yyyy')}</Typography>
          <IconButton size="small" onClick={() => onClose(id)} sx={{ color: 'white' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>{children}</DialogContent>
      </Dialog>
    );
  }

  return (
    <Paper ref={panelRef} elevation={8} onMouseDown={() => onFocus(id)} sx={{ position: 'fixed', left: position.x, top: position.y, width: 580, minHeight: 300, maxHeight: '80vh', zIndex: 1300 + zIndex, resize: 'both', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box onMouseDown={handleMouseDown} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#4869EE', color: 'white', cursor: isDragging ? 'grabbing' : 'grab', userSelect: 'none' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <DragIndicator />
          <Typography variant="h6" fontWeight={600}>{date && format(date, 'EEEE, dd MMMM yyyy')}</Typography>
        </Box>
        <IconButton size="small" onClick={() => onClose(id)} sx={{ color: 'white' }}><Close /></IconButton>
      </Box>
      <Box className="panel-content" sx={{ flex: 1, overflow: 'auto', p: 2 }}>{children}</Box>
    </Paper>
  );
}

function DemCalendar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState(isMobile ? 'day' : 'month');
  const [databases, setDatabases] = useState([]);
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDatabase, setSelectedDatabase] = useState(null);
  const [formData, setFormData] = useState({ campaign_id: null, virtual_list_id: '', send_time: '' });
  const [openPanels, setOpenPanels] = useState([]);
  const [panelCounter, setPanelCounter] = useState(0);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editCampaigns, setEditCampaigns] = useState([]);

  useEffect(() => { fetchDatabases(); }, []);
  useEffect(() => { if (databases.length > 0) fetchCalendarEntries(); }, [currentDate, viewMode, databases]);

  const fetchDatabases = async () => {
    try { const res = await axios.get('/admin/dem/databases'); setDatabases(res.data.data || []); }
    catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const fetchCalendarEntries = async () => {
    try {
      const { start, end } = getDateRange();
      const res = await axios.get('/admin/dem/calendar', { params: { start_date: format(start, 'yyyy-MM-dd'), end_date: format(end, 'yyyy-MM-dd') } });
      setCalendarEntries(res.data.data || []);
    } catch (error) { console.error('Error:', error); }
  };

  const fetchCampaignsForDatabase = async (databaseId) => {
    try { const res = await axios.get('/admin/dem/campaigns', { params: { database_id: databaseId } }); return res.data.data || []; }
    catch (error) { console.error('Error:', error); return []; }
  };

  const getDateRange = () => {
    if (viewMode === 'week') return { start: startOfWeek(currentDate, { weekStartsOn: 1 }), end: endOfWeek(currentDate, { weekStartsOn: 1 }) };
    if (viewMode === 'day') return { start: currentDate, end: currentDate };
    return { start: startOfMonth(currentDate), end: endOfMonth(currentDate) };
  };

  const getDaysArray = () => {
    if (viewMode === 'day') return [currentDate];
    const { start, end } = getDateRange();
    const days = [];
    let day = viewMode === 'month' ? startOfWeek(start, { weekStartsOn: 1 }) : start;
    const endDay = viewMode === 'month' ? endOfWeek(end, { weekStartsOn: 1 }) : end;
    while (day <= endDay) { days.push(new Date(day)); day = addDays(day, 1); }
    return days;
  };

  const getEntriesForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarEntries.filter(e => (e.send_date ? e.send_date.substring(0, 10) : '') === dateStr);
  };

  const getEntriesForDayAndDb = (date, dbId) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarEntries.filter(e => (e.send_date ? e.send_date.substring(0, 10) : '') === dateStr && e.database_id === dbId);
  };

  const getEntriesGroupedByDb = (date) => {
    const entries = getEntriesForDay(date);
    const grouped = {};
    entries.forEach(e => { if (!grouped[e.database_id]) grouped[e.database_id] = { name: e.database_name, entries: [] }; grouped[e.database_id].entries.push(e); });
    return grouped;
  };

  const checkDuplicateSameDay = (dbId, campaignId, date, virtualListId = null) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return calendarEntries.some(e => (e.send_date ? e.send_date.substring(0, 10) : '') === dateStr && e.database_id === dbId && e.campaign_id === campaignId && (virtualListId ? e.virtual_list_id === virtualListId : true));
  };

  const checkDuplicateSameWeek = (dbId, campaignId, date) => {
    const sevenDaysAgo = addDays(date, -7);
    const sevenDaysAhead = addDays(date, 7);
    return calendarEntries.filter(e => {
      const entryDate = e.send_date ? new Date(e.send_date.substring(0, 10)) : null;
      if (!entryDate) return false;
      if (entryDate.getTime() === date.getTime()) return false; // Exclude same day
      return entryDate >= sevenDaysAgo && entryDate <= sevenDaysAhead && e.database_id === dbId && e.campaign_id === campaignId;
    });
  };

  const handleOpenAddDialog = async (date, database = null) => {
    setSelectedDate(date);
    setSelectedDatabase(database);
    setFormData({ campaign_id: null, virtual_list_id: '', send_time: '' });
    if (database) {
      const camps = await fetchCampaignsForDatabase(database.id);
      setCampaigns(camps);
      setFormData(f => ({ ...f, virtual_list_id: database.virtual_lists?.[0]?.id || '' }));
    } else { setCampaigns([]); }
    setAddDialogOpen(true);
  };

  const handleDatabaseSelect = async (dbId) => {
    const db = databases.find(d => d.id === dbId);
    setSelectedDatabase(db);
    const camps = await fetchCampaignsForDatabase(dbId);
    setCampaigns(camps);
    setFormData({ campaign_id: null, virtual_list_id: db?.virtual_lists?.[0]?.id || '', send_time: '' });
  };

  const handleCampaignSelect = (campaign) => {
    if (!campaign) { setFormData({ ...formData, campaign_id: null }); setMessage({ type: '', text: '' }); return; }
        if (campaign.cap_reached) { setMessage({ type: 'error', text: 'Campaign has reached its cap and cannot be scheduled' }); return; }
     if (selectedDatabase) {
      const weekDuplicates = checkDuplicateSameWeek(selectedDatabase.id, campaign.campaign_id, selectedDate);
      if (weekDuplicates.length > 0) {
        const dates = weekDuplicates.map(e => format(new Date(e.send_date.substring(0, 10)), 'dd/MM')).join(', ');
        setMessage({ type: 'warning', text: `Already scheduled this week on ${dates}` });
      } else { setMessage({ type: '', text: '' }); }
    }
    setFormData({ ...formData, campaign_id: campaign.campaign_id });
  };

  const handleAddEntry = async () => {
    if (!formData.campaign_id || !selectedDatabase) { setMessage({ type: 'error', text: 'Select database and campaign' }); return; }
    if (checkDuplicateSameDay(selectedDatabase.id, formData.campaign_id, selectedDate, formData.virtual_list_id)) { setMessage({ type: 'error', text: 'Already scheduled on this VL today!' }); return; }
    try {
      await axios.post('/admin/dem/calendar', { database_id: selectedDatabase.id, campaign_id: formData.campaign_id, virtual_list_id: formData.virtual_list_id || null, send_date: format(selectedDate, 'yyyy-MM-dd'), send_time: formData.send_time || null });
      setMessage({ type: 'success', text: 'Entry added' });
      setAddDialogOpen(false);
      await fetchCalendarEntries();
    } catch (error) { setMessage({ type: 'error', text: 'Error adding entry' }); }
  };

  const handleDeleteEntry = async (entryId, e) => {
    if (e) e.stopPropagation();
    try { await axios.delete(`/admin/dem/calendar/${entryId}`); await fetchCalendarEntries(); }
    catch (error) { setMessage({ type: 'error', text: 'Error deleting' }); }
  };


const handleOpenDayDetail = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const existingPanel = openPanels.find(p => format(p.date, 'yyyy-MM-dd') === dateStr);
    if (existingPanel) { setOpenPanels(panels => panels.map(p => ({ ...p, zIndex: p.id === existingPanel.id ? openPanels.length : p.zIndex }))); return; }
    
    const panelWidth = 580;
    const sidebarWidth = 260;
    const screenWidth = window.innerWidth;
    const availableWidth = screenWidth - sidebarWidth;
    const panelGap = 20;
    const startY = 120;
    
    let newX, newY;
    const panelIndex = openPanels.length;
    
    if (panelIndex === 0) {
      // First panel on the left
      newX = sidebarWidth + panelGap;
      newY = startY;
    } else if (panelIndex === 1) {
      // Second panel on the right
      newX = screenWidth - panelWidth - panelGap;
      newY = startY;
    } else {
      // Additional panels stack below with offset
      const row = Math.floor((panelIndex - 2) / 2);
      const col = (panelIndex - 2) % 2;
      newX = col === 0 ? sidebarWidth + panelGap : screenWidth - panelWidth - panelGap;
      newY = startY + 380 + (row * 380);
    }
    
    const newPanel = { id: panelCounter, date: date, position: { x: newX, y: newY }, zIndex: openPanels.length };
    setPanelCounter(c => c + 1);
    setOpenPanels(panels => [...panels, newPanel]);
    setEditingEntry(null);
  };


  const handleClosePanel = (panelId) => setOpenPanels(panels => panels.filter(p => p.id !== panelId));
  const handleFocusPanel = (panelId) => setOpenPanels(panels => { const maxZ = Math.max(...panels.map(p => p.zIndex)); return panels.map(p => ({ ...p, zIndex: p.id === panelId ? maxZ + 1 : p.zIndex })); });
  const handlePanelPositionChange = (panelId, newPosition) => setOpenPanels(panels => panels.map(p => p.id === panelId ? { ...p, position: newPosition } : p));

  const handleStartEdit = async (entry, e) => {
    if (e) e.stopPropagation();
    const db = databases.find(d => d.id === entry.database_id);
    const camps = await fetchCampaignsForDatabase(entry.database_id);
    setEditCampaigns(camps);
    setEditingEntry(entry.id);
    setEditFormData({ campaign_id: entry.campaign_id, virtual_list_id: entry.virtual_list_id || '', send_time: entry.send_time || '', database: db });
  };

  const handleSaveEdit = async (entryId) => {
    try {
      await axios.put(`/admin/dem/calendar/${entryId}`, { campaign_id: editFormData.campaign_id, virtual_list_id: editFormData.virtual_list_id || null, send_time: editFormData.send_time || null });
      setEditingEntry(null);
      await fetchCalendarEntries();
    } catch (error) { setMessage({ type: 'error', text: 'Error updating' }); }
  };

  const navigateDate = (direction) => {
    if (viewMode === 'month') setCurrentDate(direction === 'next' ? addMonths(currentDate, 1) : subMonths(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(direction === 'next' ? addDays(currentDate, 7) : addDays(currentDate, -7));
    else setCurrentDate(direction === 'next' ? addDays(currentDate, 1) : addDays(currentDate, -1));
  };

  const isWeekend = (date) => { const d = getDay(date); return d === 0 || d === 6; };

  const renderCampaignChip = (entry, showActions = false, onEdit, onDelete) => {
    const colors = getVLColors(entry.virtual_list_name);
    return (
      <Box key={entry.id} component="span" sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5, mb: 0.5 }}>
        <Chip label={`${entry.campaign_name?.substring(0, isMobile ? 8 : 12)} (${entry.virtual_list_name || 'No VL'}${entry.send_time ? ', ' + entry.send_time.substring(0, 5) : ''}, €${entry.payout || 0})`} size="small" sx={{ bgcolor: colors.light, '& .MuiChip-label': { fontWeight: 500, fontSize: { xs: '0.65rem', sm: '0.75rem' } } }} />
        {showActions && (
          <>
            <IconButton size="small" onClick={(e) => onEdit(entry, e)} sx={{ ml: 0.25, p: 0.25 }}><Edit sx={{ fontSize: 14 }} /></IconButton>
            <IconButton size="small" color="error" onClick={(e) => onDelete(entry.id, e)} sx={{ p: 0.25 }}><Delete sx={{ fontSize: 14 }} /></IconButton>
          </>
        )}
      </Box>
    );
  };

  const renderPanelContent = (panelDate) => {
    return databases.map((db) => {
      const entries = panelDate ? getEntriesForDayAndDb(panelDate, db.id) : [];
      return (
        <Box key={db.id} sx={{ mb: 2 }}>
          <Box display="flex" alignItems="flex-start" flexWrap="wrap" gap={1} sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: { xs: 80, sm: 120 }, pt: 0.5, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{db.name}:</Typography>
            <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
              {entries.length === 0 ? <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.8rem' }}>No campaigns</Typography> : entries.map(entry => {
                if (editingEntry === entry.id) {
                  return (
                    <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mr: 2, mb: 1 }}>
                      <Autocomplete size="small" options={editCampaigns} value={editCampaigns.find(c => c.campaign_id === editFormData.campaign_id) || null} getOptionLabel={(o) => o.name} onChange={(e, v) => setEditFormData({ ...editFormData, campaign_id: v?.campaign_id })} renderInput={(params) => <TextField {...params} label="Campaign" />} sx={{ minWidth: { xs: 140, sm: 180 } }} slotProps={{ popper: { sx: { zIndex: 1600 } } }} />
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <InputLabel>VL</InputLabel>
                          <Select value={editFormData.virtual_list_id} onChange={(e) => setEditFormData({ ...editFormData, virtual_list_id: e.target.value })} label="VL" MenuProps={{ sx: { zIndex: 1600 } }}>
                          <MenuItem value="">None</MenuItem>
                          {editFormData.database?.virtual_lists?.map(vl => <MenuItem key={vl.id} value={vl.id}>{vl.name}</MenuItem>)}
                        </Select>
                      </FormControl>
                      {!isMobile && <TextField size="small" type="time" value={editFormData.send_time} onChange={(e) => setEditFormData({ ...editFormData, send_time: e.target.value })} InputLabelProps={{ shrink: true }} sx={{ width: 100 }} />}
                      <IconButton color="primary" size="small" onClick={() => handleSaveEdit(entry.id)}><Save fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => setEditingEntry(null)}><Cancel fontSize="small" /></IconButton>
                    </Box>
                  );
                }
                return renderCampaignChip(entry, true, handleStartEdit, handleDeleteEntry);
              })}
            </Box>
            <Button size="small" startIcon={<Add />} onClick={() => handleOpenAddDialog(panelDate, db)} sx={{ fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Add</Button>
          </Box>
        </Box>
      );
    });
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  const days = getDaysArray();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
          <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>DEM Calendar</Typography>
          <Box display="flex" gap={1} alignItems="center">
            {viewMode === 'day' && (
              <DatePicker open={datePickerOpen} onOpen={() => setDatePickerOpen(true)} onClose={() => setDatePickerOpen(false)} value={currentDate} onChange={(newDate) => { setCurrentDate(newDate); setDatePickerOpen(false); }} slotProps={{ textField: { size: 'small', sx: { width: { xs: 120, sm: 150 } } } }} />
            )}
            <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
              {!isMobile && <ToggleButton value="month"><CalendarMonth sx={{ fontSize: 18 }} /></ToggleButton>}
              {!isMobile && <ToggleButton value="week"><ViewWeek sx={{ fontSize: 18 }} /></ToggleButton>}
              <ToggleButton value="day"><Today sx={{ fontSize: 18 }} /></ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

        {databases.length === 0 ? <Alert severity="info">No databases. Add one first.</Alert> : (
          <>
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ py: 1, px: { xs: 1, sm: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <IconButton onClick={() => navigateDate('prev')} size="small"><ChevronLeft /></IconButton>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                    {viewMode === 'month' && format(currentDate, 'MMMM yyyy')}
                    {viewMode === 'week' && `${format(getDateRange().start, 'dd MMM')} - ${format(getDateRange().end, 'dd MMM')}`}
                    {viewMode === 'day' && format(currentDate, isMobile ? 'EEE, dd MMM' : 'EEEE, dd MMMM yyyy')}
                  </Typography>
                  <IconButton onClick={() => navigateDate('next')} size="small"><ChevronRight /></IconButton>
                </Box>
              </CardContent>
            </Card>

            <Paper sx={{ overflow: 'auto' }}>
              {viewMode === 'day' ? (
                <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                  {databases.map((db) => {
                    const entries = getEntriesForDayAndDb(currentDate, db.id);
                    return (
                      <Box key={db.id} sx={{ mb: 2 }}>
                        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 1 }}>
                          <Typography variant="subtitle2" fontWeight={700} sx={{ minWidth: { xs: 80, sm: 120 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{db.name}:</Typography>
                          {entries.length === 0 ? <Typography color="text.secondary" variant="body2" sx={{ fontSize: '0.8rem' }}>No campaigns</Typography> : entries.map(entry => renderCampaignChip(entry, true, handleStartEdit, handleDeleteEntry))}
                          <Button size="small" startIcon={<Add />} onClick={() => handleOpenAddDialog(currentDate, db)} sx={{ ml: 'auto', fontSize: { xs: '0.7rem', sm: '0.8rem' } }}>Add</Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : viewMode === 'week' ? (
                <Box sx={{ minWidth: 900 }}>
                  <Grid container sx={{ bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                    <Grid item sx={{ width: 140, p: 1, borderRight: '1px solid #ddd' }}><Typography variant="subtitle2" fontWeight={700}>Database</Typography></Grid>
                    {days.map((day, idx) => (
                      <Grid item key={idx} sx={{ flex: 1, p: 1, textAlign: 'center', borderRight: idx < 6 ? '1px solid #ddd' : 'none', bgcolor: isWeekend(day) ? '#fafafa' : 'inherit' }}>
                        <Typography variant="subtitle2" fontWeight={700}>{format(day, 'EEE dd')}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                  {databases.map((db) => (
                    <Grid container key={db.id} sx={{ borderBottom: '1px solid #eee' }}>
                      <Grid item sx={{ width: 140, p: 1, borderRight: '1px solid #ddd', bgcolor: '#fafafa' }}><Typography variant="body2" fontWeight={600}>{db.name}</Typography></Grid>
                      {days.map((day, dayIdx) => {
                        const entries = getEntriesForDayAndDb(day, db.id);
                        return (
                          <Grid item key={dayIdx} sx={{ flex: 1, p: 0.5, borderRight: dayIdx < 6 ? '1px solid #eee' : 'none', bgcolor: isWeekend(day) ? '#fafafa' : 'inherit', minHeight: 80, cursor: 'pointer', '&:hover': { bgcolor: '#f0f7ff' } }} onClick={() => handleOpenDayDetail(day)}>
                            {entries.map((entry) => { const colors = getVLColors(entry.virtual_list_name); return <Chip key={entry.id} label={entry.campaign_name?.substring(0, 12)} size="small" onDelete={(e) => handleDeleteEntry(entry.id, e)} sx={{ m: 0.25, bgcolor: colors.light, border: `1px solid ${colors.dark}`, '& .MuiChip-label': { fontWeight: 600, fontSize: '0.65rem' } }} />; })}
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenAddDialog(day, db); }} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}><Add sx={{ fontSize: 16 }} /></IconButton>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ))}
                </Box>
              ) : (
                <Box sx={{ minWidth: 800 }}>
                  <Grid container sx={{ bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d, idx) => (
                      <Grid item key={idx} sx={{ flex: 1, p: 1, textAlign: 'center', borderRight: idx < 6 ? '1px solid #ddd' : 'none' }}><Typography variant="subtitle2" fontWeight={700}>{d}</Typography></Grid>
                    ))}
                  </Grid>
                  {Array.from({ length: Math.ceil(days.length / 7) }).map((_, weekIdx) => (
                    <Grid container key={weekIdx} sx={{ borderBottom: '1px solid #eee' }}>
                      {days.slice(weekIdx * 7, weekIdx * 7 + 7).map((day, dayIdx) => {
                        const isCurrentMonth = isSameMonth(day, currentDate);
                        const isToday = isSameDay(day, new Date());
                        const grouped = getEntriesGroupedByDb(day);
                        const dbCount = Object.keys(grouped).length;
                        return (
                          <Grid item key={dayIdx} sx={{ flex: 1, p: 0.5, borderRight: dayIdx < 6 ? '1px solid #eee' : 'none', bgcolor: !isCurrentMonth ? '#f9f9f9' : isWeekend(day) ? '#fafafa' : 'inherit', opacity: isCurrentMonth ? 1 : 0.4, minHeight: Math.max(90, 35 + dbCount * 24), cursor: isCurrentMonth ? 'pointer' : 'default', '&:hover': { bgcolor: isCurrentMonth ? '#f0f7ff' : undefined } }} onClick={() => isCurrentMonth && handleOpenDayDetail(day)}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.25}>
                              <Typography variant="body2" fontWeight={isToday ? 700 : 500} sx={{ fontSize: '0.75rem', color: isToday ? 'primary.main' : 'inherit', bgcolor: isToday ? '#e3f2fd' : 'transparent', px: 0.5, borderRadius: 1 }}>{format(day, 'd')}</Typography>
                              {isCurrentMonth && <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenAddDialog(day); }} sx={{ opacity: 0.5, '&:hover': { opacity: 1 }, p: 0 }}><Add sx={{ fontSize: 16 }} /></IconButton>}
                            </Box>
                            {Object.entries(grouped).map(([dbId, data]) => (
                              <Box key={dbId} sx={{ mb: 0.25 }}>
                                <Typography variant="caption" component="div" sx={{ fontSize: '0.6rem', lineHeight: 1.4 }}>
                                  <strong>{data.name?.substring(0, 8)}</strong>:{' '}
                                  {data.entries.map((e) => { const colors = getVLColors(e.virtual_list_name); return <Box key={e.id} component="span" sx={{ bgcolor: colors.light, borderBottom: `2px solid ${colors.dark}`, px: 0.25, borderRadius: 0.5, mr: 0.25 }}>{e.campaign_name?.substring(0, 8)}</Box>; })}
                                </Typography>
                              </Box>
                            ))}
                          </Grid>
                        );
                      })}
                    </Grid>
                  ))}
                </Box>
              )}
            </Paper>
          </>
        )}

        {/* Add Dialog - z-index higher than panels */}
        <Dialog 
          open={addDialogOpen} 
          onClose={() => { setAddDialogOpen(false); setMessage({ type: '', text: '' }); }} 
          maxWidth="sm" 
          fullWidth 
          fullScreen={isMobile}
          sx={{ zIndex: 1500 }}
        >
          <DialogTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>Add Campaign - {selectedDate && format(selectedDate, 'dd MMM yyyy')}</DialogTitle>
          <DialogContent>
            {message.text && <Alert severity={message.type} sx={{ mb: 2, mt: 1 }}>{message.text}</Alert>}
            {!selectedDatabase && (
              <FormControl fullWidth sx={{ mt: 2, mb: 2 }} size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Database</InputLabel>
                  <Select value="" onChange={(e) => handleDatabaseSelect(e.target.value)} label="Database" MenuProps={{ sx: { zIndex: 1600 } }}>
                  {databases.map(db => <MenuItem key={db.id} value={db.id}>{db.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {selectedDatabase && (
              <>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1, mb: 2 }}>Database: {selectedDatabase.name}</Typography>
                                <Autocomplete size={isMobile ? 'small' : 'medium'} options={campaigns} getOptionLabel={(o) => `${o.name} (€${o.payout || 0})${o.cap_reached ? ' - CAP REACHED' : ''}`} onChange={(e, v) => handleCampaignSelect(v)} renderInput={(params) => <TextField {...params} label="Campaign" />} renderOption={(props, o) => <li {...props} key={o.campaign_id} style={o.cap_reached ? { opacity: 0.5 } : {}}><Box><Typography variant="body2" sx={o.cap_reached ? { textDecoration: 'line-through', color: 'error.main' } : {}}>{o.name}{o.cap_reached ? ' (CAP REACHED)' : ''}</Typography><Typography variant="caption" color={o.cap_reached ? 'error.main' : 'text.secondary'}>€{o.payout || 0}</Typography></Box></li>} noOptionsText="No campaigns" sx={{ mb: 2 }} slotProps={{ popper: { sx: { zIndex: 1600 } } }} />
                <FormControl fullWidth sx={{ mb: 2 }} size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Virtual List</InputLabel>
                    <Select value={formData.virtual_list_id} onChange={(e) => setFormData({ ...formData, virtual_list_id: e.target.value })} label="Virtual List" MenuProps={{ sx: { zIndex: 1600 } }}>
                    <MenuItem value="">None</MenuItem>
                    {selectedDatabase.virtual_lists?.map(vl => <MenuItem key={vl.id} value={vl.id}>{vl.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField fullWidth label="Send Time" type="time" value={formData.send_time} onChange={(e) => setFormData({ ...formData, send_time: e.target.value })} InputLabelProps={{ shrink: true }} size={isMobile ? 'small' : 'medium'} />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => { setAddDialogOpen(false); setMessage({ type: '', text: '' }); }}>Cancel</Button>
            <Button variant="contained" onClick={handleAddEntry} disabled={!selectedDatabase || !formData.campaign_id}>Add</Button>
          </DialogActions>
        </Dialog>

        {/* Draggable Day Detail Panels */}
        {openPanels.map((panel) => (
          <DraggablePanel key={panel.id} id={panel.id} date={panel.date} position={panel.position} zIndex={panel.zIndex} onClose={handleClosePanel} onFocus={handleFocusPanel} onPositionChange={handlePanelPositionChange} isMobile={isMobile}>
            {renderPanelContent(panel.date)}
          </DraggablePanel>
        ))}
      </Box>
    </LocalizationProvider>
  );
}

export default DemCalendar;
